import React, { useState, useRef, useEffect } from 'react'
import { Send, Bot, User, BookOpen, Loader2, MessageSquare, ArrowLeft, AlertTriangle, Shield, Lock } from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { Link } from 'react-router-dom'
import { aiTutorService } from '../../services/ai/AITutorService'
import { useAuth } from '../../contexts/AuthContext'
import aiUsageService from '../../services/ai/AIUsageService'
import { AIUsageStatus } from '../../types/ai'
import { isProUser } from '../../utils/proUtils'
import ProUpgradeModal from '../../components/auth/ProUpgradeModal'

interface Message {
  id: string
  type: 'user' | 'ai'
  content: string
  timestamp: Date
}

const AIHoiDap: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: 'Xin chào! Tôi là PhyMentor - AI Gia sư Vật lí của bạn. Tôi có thể giúp bạn giải đáp các câu hỏi về nội dung bài học Vật lí từ lớp 10-12. Hãy hỏi tôi bất kỳ điều gì!',
      timestamp: new Date()
    }
  ])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [usageStatus, setUsageStatus] = useState<AIUsageStatus | null>(null)
  const [usageError, setUsageError] = useState<string | null>(null)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()

  const isPro = isProUser(user)
  const hasReachedLimit = usageStatus && usageStatus.remaining <= 0;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (!user) {
      setUsageStatus(null)
      setUsageError('Bạn cần đăng nhập để sử dụng Hỏi đáp AI.')
      return
    }
    setUsageError(null)
    refreshUsageStatus()
  }, [user?.id])

  const refreshUsageStatus = async (force = false) => {
    if (!user) return
    try {
      const status = await aiUsageService.getStatus(user.id, force)
      setUsageStatus(status)
    } catch (error) {
      setUsageError(
        error instanceof Error ? error.message : 'Không thể tải trạng thái lượt hỏi AI.'
      )
    }
  }

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return
    if (!user) {
      setUsageError('Bạn cần đăng nhập để tiếp tục trò chuyện với AI.')
      return
    }

    if (hasReachedLimit) {
      if (!isPro) {
        setIsUpgradeModalOpen(true)
        return
      } else {
        setUsageError('Bạn đã sử dụng hết lượt hỏi AI cho hôm nay.')
        return
      }
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    const currentQuestion = inputMessage.trim()
    setInputMessage('')
    setIsLoading(true)

    try {
      const aiResponseContent = await generateAIResponse(currentQuestion)
      
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: aiResponseContent,
        timestamp: new Date()
      }
      setMessages(prev => [...prev, aiResponse])
      await refreshUsageStatus(true)
    } catch (error) {
      console.error('Lỗi khi xử lý câu hỏi:', error)
      const errorResponse: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: 'Xin lỗi, có lỗi xảy ra khi xử lý câu hỏi của bạn. Vui lòng thử lại sau.',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorResponse])
    } finally {
      setIsLoading(false)
    }
  }

  // Gọi AI Tutor Service để tạo câu trả lời
  const generateAIResponse = async (question: string): Promise<string> => {
    try {
      const response = await aiTutorService.askQuestion(question, undefined, { userId: user?.id })
      return response
    } catch (error) {
      console.error('Lỗi khi gọi AI Tutor Service:', error)
      const errorMsg = error instanceof Error ? error.message : 'Có lỗi xảy ra khi kết nối với AI'
      return `❌ **Lỗi hệ thống**\n\n${errorMsg}\n\n*Vui lòng thử lại sau hoặc liên hệ hỗ trợ nếu vấn đề vẫn tiếp tục.*`
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Quay lại trang chủ
          </Link>
          
          <div className="text-center">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg font-bold rounded-full mb-4 shadow-lg">
              <Bot className="w-6 h-6 mr-3" />
              PhyMentor - AI Gia sư Vật lí
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-700 mb-2">
              Hỏi đáp bài học
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Đặt câu hỏi về nội dung bài học Vật lí từ lớp 10-12. AI sẽ giúp bạn hiểu rõ hơn về các khái niệm và công thức.
            </p>
            
            {/* AI Status Indicator */}
            <div className="mt-4 space-y-2">
              {hasReachedLimit && !isPro && user && (
                <button 
                  onClick={() => setIsUpgradeModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 text-sm font-bold rounded-full hover:bg-orange-200 transition-colors shadow-sm border border-orange-200"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Hết lượt miễn phí. Nâng cấp Ebook PRO để tiếp tục
                </button>
              )}
              
              {!hasReachedLimit && (
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  PhyMentor đang hoạt động với API OpenAI
                </div>
              )}

              {user && usageStatus && (
                <div className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-full ${hasReachedLimit ? 'bg-red-50 text-red-800' : 'bg-blue-50 text-blue-800'}`}>
                  <Shield className="w-4 h-4 mr-2" />
                  Còn {usageStatus.remaining}/{usageStatus.dailyLimit} lượt hỏi AI hôm nay
                </div>
              )}
              {!user && (
                <div className="inline-flex items-center px-4 py-2 bg-yellow-50 text-yellow-800 text-sm font-medium rounded-full">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Đăng nhập để lưu lượt hỏi AI theo tài khoản
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden relative">
          {/* Lock Overlay for non-pro ONLY when limit reached */}
          {hasReachedLimit && !isPro && user && (
            <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[2px] flex flex-col items-center justify-center p-6 text-center">
              <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Đã hết lượt hỏi miễn phí</h3>
              <p className="text-gray-600 max-w-sm mb-6">Vui lòng nâng cấp Ebook PRO để tiếp tục sử dụng AI Gia sư và nhận thêm nhiều lượt hỏi mỗi ngày.</p>
              <button 
                onClick={() => setIsUpgradeModalOpen(true)}
                className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
              >
                Nâng cấp ngay
              </button>
            </div>
          )}

          {/* Messages */}
          <div className="h-96 md:h-[500px] overflow-y-auto p-6 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start max-w-xs md:max-w-md lg:max-w-lg ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.type === 'user' 
                      ? 'bg-blue-500 text-white ml-3' 
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white mr-3'
                  }`}>
                    {message.type === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  
                  {/* Message Content */}
                  <div className={`px-4 py-3 rounded-2xl ${
                    message.type === 'user'
                      ? 'bg-blue-500 text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    <p className={`text-xs mt-2 ${
                      message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {message.timestamp.toLocaleTimeString('vi-VN', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-start max-w-xs md:max-w-md lg:max-w-lg">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white mr-3 flex items-center justify-center">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">AI đang suy nghĩ...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4">
            {usageError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <span>{usageError}</span>
              </div>
            )}
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Hỏi về nội dung bài học Vật lí... (Ví dụ: Dao động điều hòa là gì?)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={2}
                  disabled={isLoading || !user || (hasReachedLimit && !isPro)}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading || !user || (hasReachedLimit && !isPro)}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            
            {/* Suggestions */}
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-2">Gợi ý câu hỏi:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  "Dao động điều hòa là gì?",
                  "Sóng cơ học có những đặc điểm gì?",
                  "Định luật Ohm được phát biểu như thế nào?",
                  "Nhiệt động lực học là gì?"
                ].map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (hasReachedLimit && !isPro) {
                        setIsUpgradeModalOpen(true)
                      } else {
                        setInputMessage(suggestion)
                      }
                    }}
                    className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">
                Hướng dẫn sử dụng
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Hỏi về nội dung bài học Vật lí từ lớp 10-12</li>
                <li>• PhyMentor sẽ giải thích các khái niệm, công thức và hiện tượng</li>
                <li>• Có thể hỏi về cơ học, nhiệt học, điện từ, dao động và sóng, vật lí hạt nhân</li>
                <li>• AI không thể giải bài tập cụ thể, chỉ giải thích lý thuyết</li>
                <li>• Sử dụng OpenAI GPT để cung cấp câu trả lời chính xác và chi tiết</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <ProUpgradeModal 
        isOpen={isUpgradeModalOpen} 
        onClose={() => setIsUpgradeModalOpen(false)} 
      />
    </div>
  )
}

export default AIHoiDap
